import type { LearningExercise } from "../types";

// Hagen BGB — Sachenrecht + Bereicherungsrecht + Deliktsrecht
// Abdeckung: §§ 854–856, 858–861, 868, 873, 891, 892, 903, 925, 929–936, 449,
//            §§ 985–993, 994 ff. (EBV), §§ 812–819 (Bereicherung), §§ 823–826, 831, 253 BGB.
// IDs: hj-sachen-001 … hj-sachen-042
// reviewStatus "draft" — Freigabe ausschließlich durch question-reviewer + Vincent.
export const hagenSachenrecht: LearningExercise[] = [

  // ─── MC SINGLE: Besitz ────────────────────────────────────────────────────

  {
    id: "hj-sachen-001",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Besitz",
    title: "Besitzerwerb (§ 854 BGB)",
    prompt:
      "Wie wird Besitz nach § 854 Abs. 1 BGB erworben?",
    options: [
      "Durch bloße Einigung der Parteien über den Besitzübergang.",
      "Durch Erlangung der tatsächlichen Gewalt über die Sache.",
      "Durch Eintragung im Grundbuch.",
      "Durch Übergabe einer Besitzurkunde.",
    ],
    answers: [1],
    explanation:
      "§ 854 Abs. 1 BGB: Der Besitz einer Sache wird durch die Erlangung der tatsächlichen Gewalt über die Sache erworben. Entscheidend ist die faktische Herrschaftsmacht (corpus), verbunden mit dem Beherrschungswillen (animus). Eine Einigung (wie beim Eigentumsübergang) ist für den Besitzerwerb nicht erforderlich.",
    sourceIds: ["bgb854"],
    sources: [
      { id: "bgb854", label: "§ 854 BGB", url: "https://www.gesetze-im-internet.de/bgb/__854.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "besitz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-002",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Besitz",
    title: "Besitzdiener (§ 855 BGB)",
    prompt:
      "A ist Lagerist bei der B-GmbH und hat alleinige Verfügungsgewalt über das Warenlager. Wie ist seine Stellung nach § 855 BGB zu qualifizieren?",
    options: [
      "A ist mittelbarer Besitzer, B-GmbH unmittelbarer Besitzer.",
      "A ist unmittelbarer Besitzer, B-GmbH mittelbarer Besitzer nach § 868 BGB.",
      "A ist Besitzdiener; alleiniger Besitzer ist die B-GmbH.",
      "A und B-GmbH sind Mitbesitzer nach § 866 BGB.",
    ],
    answers: [2],
    explanation:
      "§ 855 BGB: Übt jemand die tatsächliche Gewalt über eine Sache für einen anderen in dessen Haushalt oder Erwerbsgeschäft oder in einem ähnlichen Verhältnis aus, vermöge dessen er den sich auf die Sache beziehenden Weisungen des anderen Folge zu leisten hat, so ist nur der andere Besitzer. A als weisungsgebundener Lagerist ist Besitzdiener — kein Besitzer im Rechtssinne. Besitzer ist allein die B-GmbH.",
    sourceIds: ["bgb855"],
    sources: [
      { id: "bgb855", label: "§ 855 BGB", url: "https://www.gesetze-im-internet.de/bgb/__855.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "besitz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-003",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Besitz",
    title: "Mittelbarer Besitz (§ 868 BGB)",
    prompt:
      "V verleiht sein Fahrrad an M. Wer ist mittelbarer Besitzer nach § 868 BGB?",
    options: [
      "M ist mittelbarer Besitzer, weil er das Fahrrad zurückgeben muss.",
      "V ist mittelbarer Besitzer, weil er aufgrund des Leihverhältnisses einen Herausgabeanspruch gegen M hat.",
      "Weder V noch M können mittelbarer Besitzer sein, da nur unmittelbarer Besitz gesetzlich anerkannt ist.",
      "V verliert seinen Besitz vollständig mit der Übergabe an M.",
    ],
    answers: [1],
    explanation:
      "§ 868 BGB: Besitzt jemand eine Sache als Nießbraucher, Pfandgläubiger, Pächter, Mieter, Verwahrer oder in einem ähnlichen Verhältnis, vermöge dessen er einem anderen gegenüber auf Zeit zum Besitz berechtigt oder verpflichtet ist, so ist auch der andere Besitzer (mittelbarer Besitzer). M ist unmittelbarer Besitzer (hält das Rad), V ist mittelbarer Besitzer aufgrund des Leihverhältnisses (§ 598 BGB) und des daraus folgenden Herausgabeanspruchs.",
    sourceIds: ["bgb868"],
    sources: [
      { id: "bgb868", label: "§ 868 BGB", url: "https://www.gesetze-im-internet.de/bgb/__868.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["sachenrecht", "besitz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-004",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Besitz",
    title: "Verbotene Eigenmacht (§ 858 BGB)",
    prompt:
      "Was versteht man unter verbotener Eigenmacht nach § 858 Abs. 1 BGB?",
    options: [
      "Jede Besitzentziehung oder Besitzstörung ohne den Willen des Besitzers, soweit nicht das Gesetz sie gestattet.",
      "Die Entziehung von Besitz durch eine Behörde im Wege der Zwangsvollstreckung.",
      "Die Ausübung eines tatsächlichen Herrschaftswillens über eine fremde Sache mit Eigentumsanmaßung.",
      "Der Erwerb von Besitz durch einen Nichtberechtigten.",
    ],
    answers: [0],
    explanation:
      "§ 858 Abs. 1 BGB: Wer dem Besitzer ohne dessen Willen den Besitz entzieht oder ihn im Besitz stört, handelt, sofern nicht das Gesetz die Entziehung oder die Störung gestattet, widerrechtlich (verbotene Eigenmacht). Die Rechtsfolge: § 859 BGB gewährt dem Besitzer Selbsthilferechte; § 861 BGB gibt einen Anspruch auf Besitzwiedereinräumung; § 862 BGB schützt vor Besitzstörung.",
    sourceIds: ["bgb858"],
    sources: [
      { id: "bgb858", label: "§ 858 BGB", url: "https://www.gesetze-im-internet.de/bgb/__858.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "besitz"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE: Eigentumsübergang Mobilien ────────────────────────────────

  {
    id: "hj-sachen-005",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Mobiliareigentum",
    title: "Übereignung nach § 929 S. 1 BGB — Voraussetzungen",
    prompt:
      "Welche zwei Tatbestandsmerkmale nennt § 929 S. 1 BGB für die Übereignung einer beweglichen Sache?",
    options: [
      "Einigung über den Eigentumsübergang und notarielle Beurkundung.",
      "Einigung über den Eigentumsübergang und Übergabe der Sache.",
      "Kaufvertrag und Übergabe der Sache.",
      "Einigung und Eintragung im Güterrechtsregister.",
    ],
    answers: [1],
    explanation:
      "§ 929 S. 1 BGB: Zur Übertragung des Eigentums an einer beweglichen Sache ist erforderlich, dass der Eigentümer die Sache dem Erwerber übergibt und beide darüber einig sind, dass das Eigentum übergehen soll. Die Einigung ist ein dinglicher Vertrag (Einigung über den Eigentumsübergang), die Übergabe ist die Erlangung unmittelbaren Besitzes durch den Erwerber. Formfreiheit gilt hier; ein Kaufvertrag ist das Verpflichtungsgeschäft, kein Teil des Verfügungsgeschäfts.",
    sourceIds: ["bgb929"],
    sources: [
      { id: "bgb929", label: "§ 929 BGB", url: "https://www.gesetze-im-internet.de/bgb/__929.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "eigentumserwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-006",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Mobiliareigentum",
    title: "Besitzkonstitut (§ 930 BGB) — Fall",
    prompt:
      "V verkauft seine Maschine an K, will sie aber noch drei Monate nutzen und schließt mit K einen Mietvertrag darüber. Eine körperliche Übergabe findet nicht statt. Geht das Eigentum auf K über?",
    options: [
      "Nein, weil § 929 S. 1 BGB stets eine körperliche Übergabe verlangt.",
      "Nein, weil die Maschine nicht individualisiert wurde.",
      "Ja, durch Einigung und Besitzkonstitut nach § 930 BGB (Mietvertrag als Besitzmittlungsverhältnis).",
      "Ja, aber erst nach Ablauf der drei Monate, wenn V die Maschine herausgibt.",
    ],
    answers: [2],
    explanation:
      "§ 930 BGB: Ist der Eigentümer im Besitz der Sache, so kann die Übergabe dadurch ersetzt werden, dass zwischen dem Veräußerer und dem Erwerber ein Rechtsverhältnis vereinbart wird, vermöge dessen der Erwerber den mittelbaren Besitz erlangt. Das Mietverhältnis ist ein solches Besitzmittlungsverhältnis im Sinne von § 868 BGB: V wird unmittelbarer Besitzer als Mieter, K wird mittelbarer Besitzer. Eigentum geht sofort über.",
    sourceIds: ["bgb930"],
    sources: [
      { id: "bgb930", label: "§ 930 BGB", url: "https://www.gesetze-im-internet.de/bgb/__930.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "eigentumserwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-007",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Mobiliareigentum",
    title: "Abtretung des Herausgabeanspruchs (§ 931 BGB)",
    prompt:
      "E besitzt das Gemälde des V bei sich. V übereignet das Gemälde an K, ohne dass eine körperliche Übergabe stattfindet. Wie kann das Eigentum übergehen?",
    options: [
      "Durch Brevi-manu-Tradition nach § 929 S. 2 BGB.",
      "Durch Einigung und Abtretung des Herausgabeanspruchs des V gegen E an K nach § 931 BGB.",
      "Durch bloße schriftliche Einigung zwischen V und K.",
      "Gar nicht; § 929 S. 1 BGB verlangt zwingend Übergabe an K.",
    ],
    answers: [1],
    explanation:
      "§ 931 BGB: Ist ein Dritter im Besitz der Sache, so kann die Übergabe dadurch ersetzt werden, dass der Eigentümer dem Erwerber den Anspruch auf Herausgabe der Sache abtritt. V tritt seinen Herausgabeanspruch gegen E (z.B. aus § 604 BGB bei Leihe) an K ab. K wird Eigentümer, ohne die Sache je berührt zu haben. Der unmittelbare Besitz verbleibt zunächst bei E.",
    sourceIds: ["bgb931"],
    sources: [
      { id: "bgb931", label: "§ 931 BGB", url: "https://www.gesetze-im-internet.de/bgb/__931.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["sachenrecht", "eigentumserwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-008",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Gutgläubiger Erwerb",
    title: "Gutgläubiger Erwerb (§ 932 BGB) — Ausschluss bei grober Fahrlässigkeit",
    prompt:
      "K kauft von V einen Laptop, der dem D gestohlen wurde. K ist nicht positiv bösgläubig, hat aber keinerlei Nachfragen gestellt, obwohl der Preis deutlich unter Marktniveau lag. Erwirbt K gutgläubig Eigentum?",
    options: [
      "Ja, weil K keine positive Kenntnis hatte.",
      "Nein, weil der Erwerb von Diebesgut stets ausgeschlossen ist.",
      "Nein, weil K nach § 932 Abs. 2 BGB grob fahrlässig in Unkenntnis war und deshalb nicht gutgläubig ist.",
      "Ja, weil § 935 BGB nur bei abhandengekommenen Sachen gilt.",
    ],
    answers: [2],
    explanation:
      "§ 932 Abs. 2 BGB: Der Erwerber ist nicht in gutem Glauben, wenn ihm bekannt oder infolge grober Fahrlässigkeit unbekannt ist, dass die Sache nicht dem Veräußerer gehört. Grobe Fahrlässigkeit liegt vor, wenn der Erwerber naheliegende Überlegungen nicht anstellt oder ungewöhnliche Risiken außer Acht lässt. Ein deutlich unter Marktniveau liegender Preis ist ein klassisches Warnsignal, das weitere Nachfragen aufgedrängt hätte. K ist damit nicht gutgläubig; § 932 BGB schützt ihn nicht. Zusätzlich greift § 935 BGB (abhandengekommene Sache), der gutgläubigen Erwerb ohnehin ausschließt.",
    sourceIds: ["bgb932", "bgb935"],
    sources: [
      { id: "bgb932", label: "§ 932 BGB", url: "https://www.gesetze-im-internet.de/bgb/__932.html" },
      { id: "bgb935", label: "§ 935 BGB", url: "https://www.gesetze-im-internet.de/bgb/__935.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "gutglaeubiger-erwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-009",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Gutgläubiger Erwerb",
    title: "Abhandengekommene Sachen (§ 935 BGB) — Ausnahmen",
    prompt:
      "§ 935 Abs. 1 BGB schließt den gutgläubigen Erwerb bei abhandengekommenen Sachen grundsätzlich aus. Welche Ausnahme normiert § 935 Abs. 2 BGB?",
    options: [
      "Erwerb durch einen Kaufmann im Handelsverkehr.",
      "Erwerb im Wege öffentlicher Versteigerung, von Geld oder von Inhaberpapieren.",
      "Erwerb durch einen Minderjährigen mit Einwilligung des gesetzlichen Vertreters.",
      "Erwerb bei einem Eigentumsübergang durch Erbfall.",
    ],
    answers: [1],
    explanation:
      "§ 935 Abs. 2 BGB: Die Vorschrift des Absatzes 1 findet keine Anwendung auf Geld oder Inhaberpapiere sowie auf Sachen, die im Wege öffentlicher Versteigerung veräußert werden. Ratio: Beim Geld und Inhaberpapieren ist Verkehrsschutz vorrangig; bei der öffentlichen Versteigerung bietet das formale Verfahren Transparenz. Der gutgläubige Erwerb ist also selbst bei abhandengekommenen Sachen in diesen drei Konstellationen möglich.",
    sourceIds: ["bgb935"],
    sources: [
      { id: "bgb935", label: "§ 935 BGB", url: "https://www.gesetze-im-internet.de/bgb/__935.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Ausnahme",
    skillTags: ["sachenrecht", "gutglaeubiger-erwerb"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE: EBV ───────────────────────────────────────────────────────

  {
    id: "hj-sachen-010",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - EBV",
    title: "Vindikationsanspruch (§ 985 BGB)",
    prompt:
      "Welche Voraussetzungen muss der Kläger für den Herausgabeanspruch nach § 985 BGB darlegen?",
    options: [
      "Eigentum des Klägers, Besitz des Beklagten, kein Recht zum Besitz beim Kläger.",
      "Eigentum des Klägers, Besitz des Beklagten, kein Recht zum Besitz beim Beklagten.",
      "Besitz des Klägers, Eigentum des Beklagten, Herausgabeverlangen.",
      "Eigentum des Klägers, schuldhafter Besitzentzug durch den Beklagten.",
    ],
    answers: [1],
    explanation:
      "§ 985 BGB: Der Eigentümer kann von dem Besitzer die Herausgabe der Sache verlangen. Prüfungsschema: (1) Eigentum des Anspruchstellers; (2) Besitz des Anspruchsgegners; (3) kein Recht zum Besitz des Beklagten nach § 986 BGB. Der Beklagte kann nach § 986 Abs. 1 BGB die Herausgabe verweigern, wenn er ein Recht zum Besitz hat (z.B. Mietvertrag, Pfandrecht). Das Recht zum Besitz ist eine anspruchsvernichtende Einwendung, nicht Voraussetzung des Anspruchs selbst.",
    sourceIds: ["bgb985", "bgb986"],
    sources: [
      { id: "bgb985", label: "§ 985 BGB", url: "https://www.gesetze-im-internet.de/bgb/__985.html" },
      { id: "bgb986", label: "§ 986 BGB", url: "https://www.gesetze-im-internet.de/bgb/__986.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-011",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - EBV",
    title: "Haftung des verklagten/bösgläubigen Besitzers (§ 990 BGB)",
    prompt:
      "E ist Eigentümerin einer Uhr. B hat die Uhr im Bewusstsein, kein Eigentumsrecht zu haben (Bösgläubigkeit), in Besitz. Die Uhr wird durch leichte Fahrlässigkeit des B beschädigt. Haftet B der E?",
    options: [
      "Nein, weil nur vorsätzliche Beschädigungen eine Haftung begründen.",
      "Nein, weil die Haftung nur bei verklagten Besitzern eintritt.",
      "Ja, nach §§ 990 Abs. 1, 989 BGB haftet der bösgläubige Besitzer bereits ab Bösgläubigkeit.",
      "Ja, aber nur nach dem allgemeinen Deliktsrecht (§ 823 BGB).",
    ],
    answers: [2],
    explanation:
      "§ 990 Abs. 1 S. 1 BGB: War der Besitzer bei dem Erwerb des Besitzes nicht in gutem Glauben, so haftet er dem Eigentümer von der Zeit des Erwerbs an nach den §§ 987, 989 BGB. § 989 BGB unterwirft den bösgläubigen Besitzer der allgemeinen Schadensersatzpflicht. Der gutgläubige Besitzer hat dagegen nach § 993 Abs. 1 HS 2 BGB ein Haftungsprivileg: Er haftet weder nach den §§ 987–990 BGB noch nach den allgemeinen Vorschriften für zufälligen Untergang oder Beschädigung.",
    sourceIds: ["bgb990", "bgb989"],
    sources: [
      { id: "bgb990", label: "§ 990 BGB", url: "https://www.gesetze-im-internet.de/bgb/__990.html" },
      { id: "bgb989", label: "§ 989 BGB", url: "https://www.gesetze-im-internet.de/bgb/__989.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-012",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - EBV",
    title: "Haftungsprivileg des gutgläubigen Besitzers (§ 993 Abs. 1 HS 2 BGB)",
    prompt:
      "G hat eine Vase gutgläubig erworben, die dem E gehört. Durch Zufall zerbricht die Vase. Welche Aussage ist richtig?",
    options: [
      "G haftet nach § 823 Abs. 1 BGB, weil er das Eigentum verletzt hat.",
      "G haftet nach § 989 BGB auf Schadensersatz.",
      "G haftet nicht; § 993 Abs. 1 HS 2 BGB gewährt dem redlichen Besitzer ein Haftungsprivileg für zufälligen Untergang und Verschlechterung.",
      "G haftet nur auf den Sachwert, nicht auf Folgeschäden.",
    ],
    answers: [2],
    explanation:
      "§ 993 Abs. 1 HS 2 BGB: Für den zufälligen Untergang, den zufälligen Verlust und die zufällige Verschlechterung der Sache ist der redliche Besitzer nicht verantwortlich. Dieses Haftungsprivileg schützt den gutgläubigen Eigenbesitzer: Er haftete nur für Vorsatz und grobe Fahrlässigkeit nach deliktischen Grundsätzen, nicht nach den §§ 987 ff. BGB. Ein Schadensersatzanspruch aus § 823 Abs. 1 BGB scheitert, weil der Eigentumseingriff durch den Besitz-Rechtsschein gerechtfertigt war und kein Verschulden vorliegt.",
    sourceIds: ["bgb993"],
    sources: [
      { id: "bgb993", label: "§ 993 BGB", url: "https://www.gesetze-im-internet.de/bgb/__993.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  // reviewed (Re-Gate 2026-06-03): § 994 Abs. 2/GoA-Korrektur bestätigt.
  {
    id: "hj-sachen-013",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - EBV",
    title: "Verwendungsersatz (§ 994 BGB) — notwendige Verwendungen",
    prompt:
      "B hat das Pferd des E in Besitz und zahlt Tierarztkosten, um das Tier am Leben zu erhalten. B war bösgläubig. Kann B Ersatz verlangen?",
    options: [
      "Nein, weil bösgläubige Besitzer nie Verwendungsersatz beanspruchen können.",
      "Ja, Tierarztkosten sind notwendige Verwendungen; beim bösgläubigen Besitzer richtet sich der Ersatz aber nach § 994 Abs. 2 BGB i.V.m. den Regeln der Geschäftsführung ohne Auftrag (§§ 677 ff. BGB).",
      "Ja, aber nur nach den Bereicherungsregeln (§ 812 BGB).",
      "Nein, weil Tierhaltungskosten keine Verwendungen auf die Sache darstellen.",
    ],
    answers: [1],
    explanation:
      "§ 994 Abs. 1 BGB gewährt dem REDLICHEN, unverklagten Besitzer Ersatz für notwendige Verwendungen (gewöhnliche Erhaltungskosten ausgenommen, soweit ihm die Nutzungen verbleiben). Beim BÖSGLÄUBIGEN oder verklagten Besitzer greift § 994 Abs. 2 BGB: Für notwendige Verwendungen nach Eintritt der Rechtshängigkeit bzw. nach Beginn der Haftung (§ 990 BGB) bestimmt sich die Ersatzpflicht nach den Vorschriften über die Geschäftsführung ohne Auftrag (§§ 677 ff. BGB, insb. §§ 683, 670). Tierarztkosten zur Lebenserhaltung sind notwendige Verwendungen; der bösgläubige B kann sie daher nicht nach Abs. 1, sondern nur nach Abs. 2 (GoA) ersetzt verlangen. Abzugrenzen von § 996 BGB, der nur die NÜTZLICHEN Verwendungen betrifft.",
    sourceIds: ["bgb994"],
    sources: [
      { id: "bgb994", label: "§ 994 BGB", url: "https://www.gesetze-im-internet.de/bgb/__994.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE: Grundstücksrecht ─────────────────────────────────────────

  {
    id: "hj-sachen-014",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Grundstücksrecht",
    title: "Einigung und Eintragung (§ 873 BGB)",
    prompt:
      "Welche Voraussetzungen nennt § 873 Abs. 1 BGB für die Übertragung eines Rechts an einem Grundstück?",
    options: [
      "Notariell beurkundeter Kaufvertrag und Übergabe des Grundstücks.",
      "Einigung des Berechtigten und des anderen Teils über den Rechtseintritt und Eintragung der Rechtsänderung im Grundbuch.",
      "Einigung der Parteien und Besitzübergabe.",
      "Auflassung und notarielle Beurkundung.",
    ],
    answers: [1],
    explanation:
      "§ 873 Abs. 1 BGB: Zur Übertragung des Eigentums an einem Grundstück, zur Belastung eines Grundstücks mit einem Recht sowie zur Übertragung oder Belastung eines solchen Rechts ist die Einigung des Berechtigten und des anderen Teils über den Eintritt der Rechtsänderung und die Eintragung der Rechtsänderung in das Grundbuch erforderlich. Die Auflassung (§ 925 BGB) ist eine besondere Form der Einigung speziell für Eigentumsübertragungen; § 873 BGB ist der allgemeine Grundsatz.",
    sourceIds: ["bgb873"],
    sources: [
      { id: "bgb873", label: "§ 873 BGB", url: "https://www.gesetze-im-internet.de/bgb/__873.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "grundstuecksrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-015",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Grundstücksrecht",
    title: "Gutgläubiger Grundbucherwerb (§ 892 BGB)",
    prompt:
      "Im Grundbuch ist A als Eigentümer eingetragen, obwohl das Grundstück B gehört. K kauft gutgläubig von A. Erwirbt K Eigentum?",
    options: [
      "Nein, weil das Grundbuch nur deklaratorische Wirkung hat.",
      "Nein, weil A kein Eigentümer ist und niemanden mehr Rechte übertragen kann als er selbst hat.",
      "Ja; nach § 892 Abs. 1 BGB gilt der Inhalt des Grundbuchs gegenüber einem gutgläubigen Erwerber als richtig.",
      "Ja, aber nur wenn K den Kaufpreis vollständig bezahlt hat.",
    ],
    answers: [2],
    explanation:
      "§ 892 Abs. 1 S. 1 BGB: Zugunsten desjenigen, welcher ein Recht an einem Grundstück oder ein Recht an einem solchen Recht durch Rechtsgeschäft erwirbt, gilt der Inhalt des Grundbuchs als richtig, es sei denn, dass ein Widerspruch gegen die Richtigkeit eingetragen oder die Unrichtigkeit dem Erwerber bekannt ist. Der öffentliche Glaube des Grundbuchs legitimiert den gutgläubigen Erwerb auch vom Nichtberechtigten. K erwirbt Eigentum, weil er gutgläubig war (kein Widerspruch im Grundbuch, keine Kenntnis der Unrichtigkeit).",
    sourceIds: ["bgb892"],
    sources: [
      { id: "bgb892", label: "§ 892 BGB", url: "https://www.gesetze-im-internet.de/bgb/__892.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "grundstuecksrecht"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE: Sicherungsrechte ─────────────────────────────────────────

  {
    id: "hj-sachen-016",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Sachenrecht - Sicherungsrechte",
    title: "Eigentumsvorbehalt (§ 449 BGB)",
    prompt:
      "V liefert K eine Maschine unter einfachem Eigentumsvorbehalt. Bevor K den Kaufpreis vollständig bezahlt, eröffnet ein Gläubiger G die Zwangsvollstreckung in die Maschine. Was ist rechtlich zutreffend?",
    options: [
      "G kann die Maschine vollstrecken, weil K unmittelbarer Besitzer ist.",
      "V ist nach § 449 Abs. 1 BGB bis zur vollständigen Kaufpreiszahlung Eigentümer; G kann die Maschine nicht wirksam pfänden.",
      "K ist durch Besitzkonstitut sofort Eigentümer geworden; V hat nur einen schuldrechtlichen Rückforderungsanspruch.",
      "Der Eigentumsvorbehalt ist unwirksam, weil er nicht ins Grundbuch eingetragen wurde.",
    ],
    answers: [1],
    explanation:
      "§ 449 Abs. 1 BGB: Hat sich der Verkäufer einer beweglichen Sache das Eigentum bis zur Zahlung des Kaufpreises vorbehalten, so ist im Zweifel anzunehmen, dass das Eigentum unter der aufschiebenden Bedingung vollständiger Zahlung des Kaufpreises übertragen wird. V bleibt bis zur vollständigen Kaufpreiszahlung Eigentümer; eine Zwangsvollstreckung in die Sache durch G (Gläubiger des K) ist nach § 771 ZPO mittels Drittwiderspruchsklage abwehrbar.",
    sourceIds: ["bgb449"],
    sources: [
      { id: "bgb449", label: "§ 449 BGB", url: "https://www.gesetze-im-internet.de/bgb/__449.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sachenrecht", "sicherungsrechte"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE: Bereicherungsrecht ───────────────────────────────────────

  {
    id: "hj-sachen-017",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Bereicherungsrecht",
    title: "Leistungskondiktion vs. Eingriffskondiktion (§ 812 I BGB)",
    prompt:
      "A überweist irrtümlich 500 € an B, mit dem er keine vertragliche Beziehung hat. Auf welche Anspruchsgrundlage stützt sich A?",
    options: [
      "§ 812 Abs. 1 S. 1 Alt. 2 BGB (Nichtleistungskondiktion/Eingriffskondiktion).",
      "§ 812 Abs. 1 S. 1 Alt. 1 BGB (Leistungskondiktion), weil A eine bewusste zweckgerichtete Zuwendung vorgenommen hat.",
      "§ 812 Abs. 1 S. 2 BGB (condictio ob causam finitam).",
      "§ 823 Abs. 1 BGB, weil das Eigentum des A verletzt wurde.",
    ],
    answers: [1],
    explanation:
      "§ 812 Abs. 1 S. 1 Alt. 1 BGB (Leistungskondiktion): Wer durch die Leistung eines anderen etwas ohne rechtlichen Grund erlangt, ist ihm zur Herausgabe verpflichtet. Leistung ist jede bewusste und zweckgerichtete Mehrung fremden Vermögens. A hat bewusst an B überwiesen (Leistung), nur der Rechtsgrund fehlte (kein Vertrag, kein Anspruch). Die Nichtleistungskondiktion (Alt. 2) greift, wenn das Erlangte nicht durch eine Leistung, sondern auf sonstige Weise — insbesondere durch Eingriff in eine fremde Rechtsposition — erworben wurde.",
    sourceIds: ["bgb812"],
    sources: [
      { id: "bgb812", label: "§ 812 BGB", url: "https://www.gesetze-im-internet.de/bgb/__812.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-018",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Bereicherungsrecht",
    title: "Ausschluss der Kondiktion (§ 814 BGB)",
    prompt:
      "A zahlt eine Schuld an B, obwohl er weiß, dass er sie nicht schuldet. Kann A nach § 814 BGB das Geleistete zurückfordern?",
    options: [
      "Ja, weil § 812 BGB keine subjektiven Voraussetzungen kennt.",
      "Nein; § 814 BGB schließt die Rückforderung aus, wenn der Leistende die Nichtschuld kannte.",
      "Nein, weil eine freiwillige Zahlung stets bindend ist.",
      "Ja, aber nur innerhalb der dreijährigen Verjährungsfrist des § 195 BGB.",
    ],
    answers: [1],
    explanation:
      "§ 814 BGB: Das zum Zwecke der Erfüllung einer Verbindlichkeit Geleistete kann nicht zurückgefordert werden, wenn der Leistende gewusst hat, dass er zur Leistung nicht verpflichtet war. Ratio: Wer in Kenntnis der Nichtschuld leistet, verzichtet konkludent auf die Rückforderung (venire contra factum proprium). Die positive Kenntnis muss im Zeitpunkt der Leistung vorliegen; grob fahrlässige Unkenntnis genügt nicht.",
    sourceIds: ["bgb814"],
    sources: [
      { id: "bgb814", label: "§ 814 BGB", url: "https://www.gesetze-im-internet.de/bgb/__814.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Ausnahme",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-019",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Bereicherungsrecht",
    title: "Entreicherung (§ 818 Abs. 3 BGB)",
    prompt:
      "B hat 1.000 € ohne Rechtsgrund von A erhalten und das Geld für einen Urlaub verbraucht. B wusste nicht, dass er das Geld schuldet. Wie haftet B?",
    options: [
      "B muss 1.000 € zurückzahlen; Entreicherung schützt ihn nicht.",
      "B haftet nur auf Wertersatz (§ 818 Abs. 2 BGB) in Höhe des verbliebenen Vermögens.",
      "B kann sich nach § 818 Abs. 3 BGB auf Entreicherung berufen und muss gar nichts zurückzahlen, wenn er den Vorteil nicht mehr hat.",
      "B muss nur die Kosten des Urlaubs erstatten, nicht den vollen Betrag.",
    ],
    answers: [2],
    explanation:
      "§ 818 Abs. 3 BGB: Die Verpflichtung zur Herausgabe oder zum Ersatz des Wertes ist ausgeschlossen, soweit der Empfänger nicht mehr bereichert ist. Hat der gutgläubige Empfänger das Erlangte verbraucht, ohne dafür einen bleibenden Vermögensvorteil zu haben (Urlaub ist typisches Beispiel für konsumptive Verwendung ohne Werteersatz), ist er entreichert. Die Entreicherungseinrede steht nur dem gutgläubigen Empfänger zu; § 819 BGB entzieht sie dem bösgläubigen.",
    sourceIds: ["bgb818"],
    sources: [
      { id: "bgb818", label: "§ 818 BGB", url: "https://www.gesetze-im-internet.de/bgb/__818.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-020",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Bereicherungsrecht",
    title: "Verschärfte Haftung bei Kenntnis (§ 819 BGB)",
    prompt:
      "C hat Geld ohne Rechtsgrund erhalten und weiß dies. Er gibt das Geld trotzdem aus. Wie ändert sich seine Haftung gegenüber § 818 Abs. 3 BGB?",
    options: [
      "§ 819 BGB ändert nichts; § 818 Abs. 3 BGB gilt immer.",
      "C verliert die Entreicherungseinrede; er haftet nach § 819 Abs. 1 BGB wie ein Schuldner, der sich im Verzug befindet.",
      "C haftet nur nach Deliktsrecht (§ 823 BGB).",
      "C haftet nur, wenn er das Geld vorsätzlich veruntreut hat.",
    ],
    answers: [1],
    explanation:
      "§ 819 Abs. 1 BGB: Kennt der Empfänger den Mangel des rechtlichen Grundes bei dem Empfang oder erfährt er ihn später, so ist er von dem Empfang oder der Erlangung der Kenntnis an nach den allgemeinen Vorschriften verantwortlich. Folge: § 818 Abs. 3 BGB (Entreicherung) ist ausgeschlossen; C haftet wie ein Schuldner in Verzug, also auf alle Schäden, die auf der Nichterfüllung beruhen. Die verschärfte Haftung setzt positive Kenntnis voraus; grob fahrlässige Unkenntnis reicht nicht.",
    sourceIds: ["bgb819"],
    sources: [
      { id: "bgb819", label: "§ 819 BGB", url: "https://www.gesetze-im-internet.de/bgb/__819.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE: Deliktsrecht ──────────────────────────────────────────────

  {
    id: "hj-sachen-021",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Deliktsrecht",
    title: "Absolute Rechte und Rechtsgüter (§ 823 Abs. 1 BGB)",
    prompt:
      "Welches der folgenden Rechtsgüter ist NICHT in § 823 Abs. 1 BGB ausdrücklich genannt?",
    options: [
      "Leben",
      "Freiheit",
      "Vermögen",
      "Gesundheit",
    ],
    answers: [2],
    explanation:
      "§ 823 Abs. 1 BGB schützt ausdrücklich: Leben, Körper, Gesundheit, Freiheit, Eigentum und sonstige Rechte. Das bloße Vermögen (als solches) ist kein in § 823 Abs. 1 BGB genanntes Rechtsgut; Vermögensschäden ohne Verletzung eines der genannten Rechtsgüter sind über § 823 Abs. 1 BGB grundsätzlich nicht ersatzfähig. Schutz reiner Vermögensinteressen kann aus § 823 Abs. 2 BGB (Schutzgesetz) oder § 826 BGB (vorsätzliche sittenwidrige Schädigung) folgen.",
    sourceIds: ["bgb823"],
    sources: [
      { id: "bgb823", label: "§ 823 BGB", url: "https://www.gesetze-im-internet.de/bgb/__823.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-022",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Deliktsrecht",
    title: "Schutzgesetzverletzung (§ 823 Abs. 2 BGB)",
    prompt:
      "Fahrer F fährt mit 100 km/h durch eine 50er-Zone und verletzt P. P verlangt Schadensersatz. Auf welche Norm stützt sich der Anspruch neben § 823 Abs. 1 BGB?",
    options: [
      "§ 826 BGB, weil F vorsätzlich sittenwidrig handelte.",
      "§ 831 BGB, weil F Verrichtungsgehilfe des Fahrzeughalters ist.",
      "§ 823 Abs. 2 BGB i.V.m. § 3 Abs. 3 Nr. 1 StVO, weil die Geschwindigkeitsregel ein Schutzgesetz ist.",
      "§ 812 BGB, weil F durch die Fahrt einen ungerechtfertigten Vorteil erlangt hat.",
    ],
    answers: [2],
    explanation:
      "§ 823 Abs. 2 BGB: Wer gegen ein den Schutz eines anderen bezweckendes Gesetz verstößt, ist zum Schadensersatz verpflichtet. § 3 StVO (Höchstgeschwindigkeit) ist ein Schutzgesetz im Sinne von § 823 Abs. 2 BGB, weil die Norm gerade dazu dient, andere Verkehrsteilnehmer vor Schäden durch überhöhte Geschwindigkeit zu bewahren. Anders als bei § 823 Abs. 1 BGB (Verschulden hinsichtlich der Rechtsgutsverletzung) reicht beim Schutzgesetzverstoß die Zuwiderhandlung gegen die Norm.",
    sourceIds: ["bgb823"],
    sources: [
      { id: "bgb823", label: "§ 823 BGB", url: "https://www.gesetze-im-internet.de/bgb/__823.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  // FIX angewendet (Orchestrator 2026-06-03): Exkulpations-Subjekt in Option [1] auf den Geschäftsherrn A umgestellt (§ 831 I S. 2). reviewed bleibt.
  {
    id: "hj-sachen-023",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Deliktsrecht",
    title: "Haftung für Verrichtungsgehilfen (§ 831 BGB) — Exkulpation",
    prompt:
      "Arbeitgeber A hat Mitarbeiter M für einfache Botengänge eingestellt. M verursacht bei einer Fahrt schuldhaft einen Unfall. Wie kann A die Haftung nach § 831 Abs. 1 S. 2 BGB abwenden?",
    options: [
      "A haftet immer uneingeschränkt; eine Exkulpation ist bei § 831 BGB ausgeschlossen.",
      "A kann sich exkulpieren, wenn er beweist, dass er bei Auswahl und Überwachung des M die im Verkehr erforderliche Sorgfalt beachtet hat — oder dass der Schaden auch bei Anwendung dieser Sorgfalt entstanden wäre.",
      "A haftet nur, wenn M vorsätzlich gehandelt hat.",
      "A haftet nur in Höhe des Mindestlohns des M pro Monat.",
    ],
    answers: [1],
    explanation:
      "§ 831 Abs. 1 S. 1 BGB: Wer einen anderen zur Verrichtung bestellt, ist zum Ersatz des Schadens verpflichtet, den der andere in Ausführung der Verrichtung einem Dritten widerrechtlich zufügt. S. 2: Die Ersatzpflicht tritt nicht ein, wenn der Geschäftsherr bei der Auswahl der bestellten Person und, sofern er Vorrichtungen oder Gerätschaften zu beschaffen oder die Ausführung der Verrichtung zu leiten hat, bei der Beschaffung oder der Leitung die im Verkehr erforderliche Sorgfalt beobachtet hat oder wenn der Schaden auch bei Anwendung dieser Sorgfalt entstanden sein würde. Es handelt sich um eine Verschuldensvermutung mit Entlastungsmöglichkeit (keine Gefährdungshaftung).",
    sourceIds: ["bgb831"],
    sources: [
      { id: "bgb831", label: "§ 831 BGB", url: "https://www.gesetze-im-internet.de/bgb/__831.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-024",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Deliktsrecht",
    title: "Vorsätzliche sittenwidrige Schädigung (§ 826 BGB)",
    prompt:
      "Wann ist eine Haftung aus § 826 BGB gegeben?",
    options: [
      "Wenn jemand fahrlässig gegen ein Schutzgesetz verstößt und dadurch Schäden verursacht.",
      "Wenn jemand einem anderen vorsätzlich in einer gegen die guten Sitten verstoßenden Weise Schaden zufügt.",
      "Wenn jemand sein Eigentum in einer Weise nutzt, die andere schädigt.",
      "Wenn jemand durch arglistige Täuschung einen Vertrag erschleicht.",
    ],
    answers: [1],
    explanation:
      "§ 826 BGB: Wer in einer gegen die guten Sitten verstoßenden Weise einem anderen vorsätzlich Schaden zufügt, ist dem anderen zum Ersatz des Schadens verpflichtet. Voraussetzungen: (1) Handlung oder Unterlassen, (2) Sittenwidrigkeit (Verstoß gegen das Anstandsgefühl aller billig und gerecht Denkenden), (3) Vorsatz hinsichtlich Sittenwidrigkeit und Schadenseintritt (dolus eventualis genügt), (4) Schaden. § 826 BGB erfasst auch reine Vermögensschäden und ist damit weiter als § 823 Abs. 1 BGB.",
    sourceIds: ["bgb826"],
    sources: [
      { id: "bgb826", label: "§ 826 BGB", url: "https://www.gesetze-im-internet.de/bgb/__826.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  // ─── FLASHCARDS ───────────────────────────────────────────────────────────

  {
    id: "hj-sachen-025",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Sachenrecht - Besitz",
    title: "Besitzschutz: Ansprüche aus §§ 861, 862 BGB",
    prompt:
      "Welche Besitzschutzansprüche gewähren §§ 861 und 862 BGB und gegen wen richten sie sich?",
    options: [],
    answers: [],
    explanation:
      "§ 861 Abs. 1 BGB: Wird dem Besitzer der Besitz durch verbotene Eigenmacht entzogen, so kann er von demjenigen, welcher ihm gegenüber fehlerhaft besitzt, die Wiedereinräumung des Besitzes verlangen. § 862 Abs. 1 BGB: Wird der Besitzer durch verbotene Eigenmacht im Besitz gestört, so kann er von dem Störer die Beseitigung der Störung verlangen; sind weitere Störungen zu besorgen, so kann er auf Unterlassung klagen. Beide Ansprüche sind rein possessorisch — sie schützen den Besitz als solchen, unabhängig von der Eigentumslage. Der Anspruch verjährt nach § 864 Abs. 1 BGB in einem Jahr ab dem Entzug/der Störung.",
    sourceIds: ["bgb861", "bgb862"],
    sources: [
      { id: "bgb861", label: "§ 861 BGB", url: "https://www.gesetze-im-internet.de/bgb/__861.html" },
      { id: "bgb862", label: "§ 862 BGB", url: "https://www.gesetze-im-internet.de/bgb/__862.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "besitz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-026",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Sachenrecht - Mobiliareigentum",
    title: "Brevi-manu-Tradition (§ 929 S. 2 BGB)",
    prompt:
      "Was ist die brevi-manu-Tradition nach § 929 S. 2 BGB und wann kommt sie in Betracht?",
    options: [],
    answers: [],
    explanation:
      "§ 929 S. 2 BGB: Ist der Erwerber im Besitz der Sache, so genügt die Einigung über den Eigentumsübergang. Die Übergabe wird ersetzt, weil der Erwerber bereits unmittelbarer Besitzer ist (kurze Hand = brevi manu). Typischer Fall: der Entleiher kauft die geliehene Sache. Es sind keine weiteren Akte erforderlich; die dingliche Einigung genügt allein.",
    sourceIds: ["bgb929"],
    sources: [
      { id: "bgb929", label: "§ 929 BGB", url: "https://www.gesetze-im-internet.de/bgb/__929.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "eigentumserwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-027",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Sachenrecht - Eigentum",
    title: "Inhalt des Eigentums (§ 903 BGB)",
    prompt:
      "Was gewährt § 903 BGB dem Eigentümer einer Sache?",
    options: [],
    answers: [],
    explanation:
      "§ 903 S. 1 BGB: Der Eigentümer einer Sache kann, soweit nicht das Gesetz oder Rechte Dritter entgegenstehen, mit der Sache nach Belieben verfahren und andere von jeder Einwirkung ausschließen. Das Eigentumsrecht ist damit das umfassendste dingliche Recht: es enthält positiv das Nutzungs- und Verfügungsrecht sowie negativ das Ausschlussrecht. Beschränkungen ergeben sich aus öffentlichem Recht (z.B. Baurecht), Nachbarrecht (§§ 906 ff. BGB) und dinglichen Rechten Dritter (z.B. Dienstbarkeiten, § 1018 BGB).",
    sourceIds: ["bgb903"],
    sources: [
      { id: "bgb903", label: "§ 903 BGB", url: "https://www.gesetze-im-internet.de/bgb/__903.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "eigentumserwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-028",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Sachenrecht - Grundstücksrecht",
    title: "Grundbuchvermutung (§ 891 BGB)",
    prompt:
      "Welche Vermutung stellt § 891 BGB auf und welche Wirkung hat sie prozessual?",
    options: [],
    answers: [],
    explanation:
      "§ 891 Abs. 1 BGB: Ist im Grundbuch für jemanden ein Recht eingetragen, so wird vermutet, dass ihm das Recht zustehe. § 891 Abs. 2 BGB: Ist ein im Grundbuch eingetragenes Recht gelöscht, so wird vermutet, dass das Recht nicht bestehe. Die Vermutung ist widerlegbar (§ 292 ZPO). Prozessual entlastet sie denjenigen, dem das eingetragene Recht zukommt, von der Beweislast: Er muss sein Recht nicht beweisen, der Gegner muss die Unrichtigkeit des Grundbuchs darlegen und beweisen. Abgrenzung zu § 892 BGB (öffentlicher Glaube): § 891 BGB ist Beweisregel; § 892 BGB vermittelt materiell-rechtlichen Gutglaubensschutz.",
    sourceIds: ["bgb891"],
    sources: [
      { id: "bgb891", label: "§ 891 BGB", url: "https://www.gesetze-im-internet.de/bgb/__891.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["sachenrecht", "grundstuecksrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-029",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Bereicherungsrecht",
    title: "Saldotheorie bei nichtigen Austauschverträgen",
    prompt:
      "Was besagt die Saldotheorie im Bereicherungsrecht und auf welche Konstellation wird sie angewendet?",
    options: [],
    answers: [],
    explanation:
      "Die Saldotheorie wird bei der bereicherungsrechtlichen Rückabwicklung nichtiger gegenseitiger Verträge angewendet: Statt beiderseitiger Herausgabepflichten werden die gegenseitigen Bereicherungsansprüche (§ 812 BGB) zu einem einheitlichen Saldo verrechnet. Wer netto mehr erhalten hat, als er geleistet hat, schuldet nur die Differenz. Ratio: Vermeidung von Wertungsungereimtheiten, die entstünden, wenn nur eine Partei sich auf § 818 Abs. 3 BGB (Entreicherung) berufen könnte. Die Saldotheorie gilt nicht, wenn eine Partei nach § 819 BGB bösgläubig oder minderjährig ist.",
    sourceIds: ["bgb812", "bgb818"],
    sources: [
      { id: "bgb812", label: "§ 812 BGB", url: "https://www.gesetze-im-internet.de/bgb/__812.html" },
      { id: "bgb818", label: "§ 818 BGB", url: "https://www.gesetze-im-internet.de/bgb/__818.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-030",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Deliktsrecht",
    title: "Schmerzensgeld (§ 253 Abs. 2 BGB)",
    prompt:
      "Unter welchen Voraussetzungen kann Schmerzensgeld verlangt werden und auf welcher Norm beruht es?",
    options: [],
    answers: [],
    explanation:
      "§ 253 Abs. 2 BGB: Ist wegen einer Verletzung des Körpers, der Gesundheit, der Freiheit oder der sexuellen Selbstbestimmung Schadensersatz zu leisten, kann auch wegen des Schadens, der nicht Vermögensschaden ist, eine billige Entschädigung in Geld gefordert werden. Kombiniert mit § 823 Abs. 1 BGB entsteht der klassische Schmerzensgeldsanspruch: Der Schädiger, der eines der genannten Rechtsgüter schuldhaft verletzt, schuldet eine billige Entschädigung für immaterielle Schäden. Die Bemessung erfolgt nach Ausgleichs- und Genugtuungsfunktion (BGH-Rspr.). Reine Vermögensschäden begründen keinen § 253 Abs. 2 BGB-Anspruch.",
    sourceIds: ["bgb253", "bgb823"],
    sources: [
      { id: "bgb253", label: "§ 253 BGB", url: "https://www.gesetze-im-internet.de/bgb/__253.html" },
      { id: "bgb823", label: "§ 823 BGB", url: "https://www.gesetze-im-internet.de/bgb/__823.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-031",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Deliktsrecht",
    title: "Verkehrssicherungspflichten (§ 823 Abs. 1 BGB)",
    prompt:
      "Was sind Verkehrssicherungspflichten und wie werden sie im Rahmen von § 823 Abs. 1 BGB relevant?",
    options: [],
    answers: [],
    explanation:
      "Verkehrssicherungspflichten sind ungeschriebene Verhaltenspflichten: Wer eine Gefahrenquelle schafft oder unterhält, muss die notwendigen Vorkehrungen treffen, um Dritte vor daraus entstehenden Schäden zu schützen. Sie sind eine Konkretisierung des Fahrlässigkeitsmaßstabs im Rahmen von § 823 Abs. 1 BGB. Typische Beispiele: Streupflicht auf Gehwegen im Winter, Sicherung von Baustellen, Kontrolle von Bäumen auf Standfestigkeit. Eine Verletzung der Verkehrssicherungspflicht indiziert fahrlässiges Handeln. Der Verkehrssicherungspflichtige kann Dritte mit der Ausführung betrauen, trägt aber die Überwachungspflicht.",
    sourceIds: ["bgb823"],
    sources: [
      { id: "bgb823", label: "§ 823 BGB", url: "https://www.gesetze-im-internet.de/bgb/__823.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-032",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Bereicherungsrecht",
    title: "Wertersatz (§ 818 Abs. 2 BGB)",
    prompt:
      "Wann schuldet der Bereicherungsschuldner nach § 818 Abs. 2 BGB Wertersatz statt Herausgabe?",
    options: [],
    answers: [],
    explanation:
      "§ 818 Abs. 2 BGB: Ist die Herausgabe wegen der Beschaffenheit des Erlangten nicht möglich oder ist der Empfänger aus einem anderen Grunde zur Herausgabe außerstande, so hat er den Wert zu ersetzen. Wertersatz tritt ein, wenn (a) die Sache nicht in Natur herausgegeben werden kann (z.B. verbrauchte Energie, verbrauchtes Geld auf einem gesperrten Konto) oder (b) der Empfänger aus anderem Grund — z.B. weil die Sache untergegangen ist — nicht herausgeben kann. Maßgeblich ist der objektive Verkehrswert im Zeitpunkt des Empfangs (nicht des Urteils). Hat der Empfänger die Sache weiterverkauft, ist Wertersatz der gemeine Wert, nicht der erzielte Veräußerungserlös (sofern nicht bereichernd).",
    sourceIds: ["bgb818"],
    sources: [
      { id: "bgb818", label: "§ 818 BGB", url: "https://www.gesetze-im-internet.de/bgb/__818.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERINGS ────────────────────────────────────────────────────────────

  {
    id: "hj-sachen-033",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Sachenrecht - EBV",
    title: "Prüfschema § 985 BGB — Herausgabeanspruch",
    prompt:
      "Bringe die Prüfungsschritte des Vindikationsanspruchs (§ 985 BGB) in die richtige gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Eigentum des Anspruchstellers zum Zeitpunkt der Klage (originärer oder derivativer Eigentumserwerb nachweisen)",
      "Unmittelbarer oder mittelbarer Besitz des Anspruchsgegners (§§ 854, 868 BGB)",
      "Kein Recht zum Besitz des Anspruchsgegners (§ 986 BGB) — Einwendungsprüfung",
      "Ergebnis: Herausgabepflicht besteht; ggf. Folgeansprüche aus §§ 987 ff. BGB prüfen",
    ],
    explanation:
      "Der Vindikationsanspruch nach § 985 BGB wird dreistufig geprüft: (1) Eigentum des Klägers — Ausgangspunkt ist das ursprüngliche Eigentumsrecht und seine lückenlose Kette bis zum heutigen Zeitpunkt. (2) Besitz des Beklagten — unmittelbarer (§ 854 BGB) oder mittelbarer Besitz (§ 868 BGB). (3) Kein Recht zum Besitz nach § 986 BGB — der Beklagte trägt die Beweislast für ein Besitzrecht (z.B. Miet-, Leih- oder Pfandverhältnis). Im Anschluss folgen ggf. die EBV-Nebenansprüche (Nutzungen, Schadensersatz, Verwendungsersatz).",
    sourceIds: ["bgb985", "bgb986"],
    sources: [
      { id: "bgb985", label: "§ 985 BGB", url: "https://www.gesetze-im-internet.de/bgb/__985.html" },
      { id: "bgb986", label: "§ 986 BGB", url: "https://www.gesetze-im-internet.de/bgb/__986.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-034",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Sachenrecht - Gutgläubiger Erwerb",
    title: "Prüfschema gutgläubiger Erwerb §§ 929, 932 BGB",
    prompt:
      "Ordne die Prüfungsschritte beim gutgläubigen Eigentumserwerb vom Nichtberechtigten in der richtigen Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Einigung über den Eigentumsübergang (§ 929 S. 1 BGB)",
      "Übergabe der Sache (§ 929 S. 1 BGB) oder Übergabesurrogat (§§ 929 S. 2, 930, 931 BGB)",
      "Veräußerer ist Nichtberechtigter — gutgläubiger Erwerb nach § 932 BGB erforderlich",
      "Gutgläubigkeit des Erwerbers (kein Wissen und keine grobe Fahrlässigkeit, § 932 Abs. 2 BGB)",
      "Kein Ausschluss nach § 935 BGB (Sache nicht abhandengekommen — Ausnahme: Geld/Inhaberpapiere/öffentliche Versteigerung)",
    ],
    explanation:
      "Schema: (1) Zuerst den Grundtatbestand § 929 S. 1 BGB prüfen (Einigung + Übergabe). (2) Stellt sich heraus, dass der Veräußerer Nichtberechtigter ist, muss § 932 BGB helfen. (3) § 932 Abs. 2 BGB: Gutgläubigkeit — der Erwerber darf weder wissen noch grob fahrlässig nicht wissen, dass der Veräußerer nicht Eigentümer ist. (4) § 935 Abs. 1 BGB: Ist die Sache dem Eigentümer abhandengekommen (ohne seinen Willen), ist gutgläubiger Erwerb ausgeschlossen — Ausnahme § 935 Abs. 2 BGB (Geld, Inhaberpapiere, öffentliche Versteigerung).",
    sourceIds: ["bgb929", "bgb932", "bgb935"],
    sources: [
      { id: "bgb929", label: "§ 929 BGB", url: "https://www.gesetze-im-internet.de/bgb/__929.html" },
      { id: "bgb932", label: "§ 932 BGB", url: "https://www.gesetze-im-internet.de/bgb/__932.html" },
      { id: "bgb935", label: "§ 935 BGB", url: "https://www.gesetze-im-internet.de/bgb/__935.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["sachenrecht", "gutglaeubiger-erwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-035",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Bereicherungsrecht",
    title: "Prüfschema Leistungskondiktion (§ 812 Abs. 1 S. 1 Alt. 1 BGB)",
    prompt:
      "Bringe die Prüfungsschritte der Leistungskondiktion in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Etwas erlangt (jeder Vermögensvorteil: Sacheigentum, Geldzahlung, Dienstleistung, Forderungsbefreiung)",
      "Durch Leistung (bewusste, zweckgerichtete Mehrung fremden Vermögens zur Erfüllung einer — vermeintlichen — Verbindlichkeit)",
      "Ohne rechtlichen Grund (kein wirksamer Vertrag, keine gesetzliche Grundlage, kein anerkannter Behaltensgrund)",
      "Rechtsfolge: Herausgabe des Erlangten (§ 818 Abs. 1 BGB) oder Wertersatz (§ 818 Abs. 2 BGB); ggf. Entreicherung (§ 818 Abs. 3 BGB) oder verschärfte Haftung (§ 819 BGB)",
    ],
    explanation:
      "§ 812 Abs. 1 S. 1 Alt. 1 BGB: Etwas erlangt — Leistung — ohne rechtlichen Grund. (1) Erlangtes muss ein Vermögensvorteil sein. (2) Leistung ist die Zuwendung mit Zuweisungsbewusstsein des Leistenden; unterscheidet die Leistungskondiktion von der Eingriffskondiktion (Alt. 2). (3) Kein Rechtsgrund: kein wirksamer Kausalvertrag, keine gesetzliche Grundlage, keine sonstige Causa. (4) Rechtsfolge: primär Naturalherausgabe, subsidiär Wertersatz; Entreicherungseinrede nur für Gutgläubige (§ 818 Abs. 3 BGB); verschärfte Haftung bei Kenntnis (§ 819 BGB).",
    sourceIds: ["bgb812", "bgb818"],
    sources: [
      { id: "bgb812", label: "§ 812 BGB", url: "https://www.gesetze-im-internet.de/bgb/__812.html" },
      { id: "bgb818", label: "§ 818 BGB", url: "https://www.gesetze-im-internet.de/bgb/__818.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-036",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Deliktsrecht",
    title: "Prüfschema § 823 Abs. 1 BGB",
    prompt:
      "Ordne die Prüfungsschritte der deliktischen Haftung nach § 823 Abs. 1 BGB in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Verletzungshandlung (Tun oder pflichtwidriges Unterlassen)",
      "Verletzung eines geschützten Rechtsguts (Leben, Körper, Gesundheit, Freiheit, Eigentum oder sonstiges absolutes Recht)",
      "Haftungsbegründende Kausalität (äquivalente und adäquate Kausalität zwischen Handlung und Rechtsgutsverletzung)",
      "Rechtswidrigkeit (indiziert durch Tatbestandsverwirklichung; Rechtfertigungsgründe negieren)",
      "Verschulden (Vorsatz oder Fahrlässigkeit, § 276 BGB; Deliktsfähigkeit §§ 827, 828 BGB)",
      "Haftungsausfüllende Kausalität und Schaden (§§ 249 ff. BGB)",
    ],
    explanation:
      "Das Prüfschema des § 823 Abs. 1 BGB folgt dem klassischen Dreischritt Tatbestand-Rechtswidrigkeit-Schuld: (1) Verletzungshandlung; (2) Verletzung eines der abschließend genannten Rechtsgüter; (3) haftungsbegründende Kausalität (äquivalente Kausalität + Adäquanz + Schutzzweck der Norm); (4) Rechtswidrigkeit — indiziert durch Tatbestandsverwirklichung, zu verneinen bei Notwehr (§ 227 BGB), Notstand (§§ 228, 904 BGB), Einwilligung; (5) Verschulden — Vorsatz oder objektive und subjektive Fahrlässigkeit (§ 276 BGB), Deliktsfähigkeit prüfen; (6) Schaden und Kausalität der Pflichtverletzung zum Schaden.",
    sourceIds: ["bgb823"],
    sources: [
      { id: "bgb823", label: "§ 823 BGB", url: "https://www.gesetze-im-internet.de/bgb/__823.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-037",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Sachenrecht - EBV",
    title: "EBV-Haftungsschema: redlicher vs. bösgläubiger Besitzer",
    prompt:
      "Ordne die Prüfungsschritte zur Bestimmung der Haftungsintensität im EBV in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Qualifizierung des Besitzers: gutgläubiger (redlicher) Besitzer oder bösgläubiger/verklagter Besitzer (§§ 990, 987 BGB)?",
      "Gutgläubiger Besitzer: Haftungsprivileg nach § 993 Abs. 1 HS 2 BGB (kein Ersatz für zufälligen Untergang/Verschlechterung; keine Nutzungsherausgabe über Übermaßfrüchte hinaus)",
      "Bösgläubiger/verklagter Besitzer: Haftung nach §§ 987, 989, 990 BGB (Nutzungsherausgabe ab Rechtshängigkeit/Bösgläubigkeit; Schadensersatz für jede Fahrlässigkeit)",
      "Verwendungsersatz für alle Besitzer: notwendige Verwendungen nach § 994 BGB; nützliche Verwendungen nach § 996 BGB nur für gutgläubige Besitzer",
    ],
    explanation:
      "Das EBV-Sonderregime differenziert streng nach der Gut-/Bösgläubigkeit: Der redliche Besitzer genießt nach § 993 Abs. 1 HS 2 BGB umfassenden Schutz. Der bösgläubige oder nach Rechtshängigkeit besitzende Besitzer haftet vollständig nach §§ 987–990 BGB. Verwendungsersatz steht nach §§ 994, 996 BGB differenziert zur Verfügung: Notwendige Verwendungen ersetzen alle Besitzer; nützliche nur der gutgläubige Eigenbesitzer (§ 996 BGB). Das EBV-Regime ist gegenüber § 823 Abs. 1 BGB und § 812 BGB lex specialis.",
    sourceIds: ["bgb987", "bgb990", "bgb993", "bgb994"],
    sources: [
      { id: "bgb987", label: "§ 987 BGB", url: "https://www.gesetze-im-internet.de/bgb/__987.html" },
      { id: "bgb990", label: "§ 990 BGB", url: "https://www.gesetze-im-internet.de/bgb/__990.html" },
      { id: "bgb993", label: "§ 993 BGB", url: "https://www.gesetze-im-internet.de/bgb/__993.html" },
      { id: "bgb994", label: "§ 994 BGB", url: "https://www.gesetze-im-internet.de/bgb/__994.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  // ─── CLOZES ───────────────────────────────────────────────────────────────

  {
    id: "hj-sachen-038",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Sachenrecht - Gutgläubiger Erwerb",
    title: "Gutgläubiger Erwerb ausgeschlossen (§ 932 Abs. 2 BGB)",
    prompt:
      "Nach § 932 Abs. 2 BGB ist der Erwerber nicht in gutem Glauben, wenn ihm bekannt oder infolge ___ unbekannt ist, dass die Sache nicht dem Veräußerer gehört.",
    options: [
      "leichter Fahrlässigkeit",
      "grober Fahrlässigkeit",
      "einfacher Unkenntnis",
      "Irrtums über den Wert",
    ],
    answers: [1],
    explanation:
      "§ 932 Abs. 2 BGB: Der Erwerber ist nicht in gutem Glauben, wenn ihm bekannt oder infolge grober Fahrlässigkeit unbekannt ist, dass die Sache nicht dem Veräußerer gehört. Grobe Fahrlässigkeit liegt vor, wenn der Erwerber die im Verkehr erforderliche Sorgfalt in besonders schwerem Maße verletzt und das außer Acht lässt, was jedem hätte einleuchten müssen. Einfache (leichte) Fahrlässigkeit schließt den guten Glauben hingegen nicht aus — anders als beim Grundstückserwerb (§ 932 BGB vs. § 892 BGB, dort reicht schon leichte Fahrlässigkeit nicht mehr aus, wenn ein Widerspruch eingetragen ist).",
    sourceIds: ["bgb932"],
    sources: [
      { id: "bgb932", label: "§ 932 BGB", url: "https://www.gesetze-im-internet.de/bgb/__932.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Lücke",
    skillTags: ["sachenrecht", "gutglaeubiger-erwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-039",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Bereicherungsrecht",
    title: "Entreicherung (§ 818 Abs. 3 BGB) — Begriff",
    prompt:
      "§ 818 Abs. 3 BGB schließt die Verpflichtung zur Herausgabe oder zum Ersatz des Wertes aus, soweit der Empfänger nicht mehr ___ ist.",
    options: [
      "verschuldet",
      "bereichert",
      "zahlungsfähig",
      "bösgläubig",
    ],
    answers: [1],
    explanation:
      "§ 818 Abs. 3 BGB: Die Verpflichtung zur Herausgabe oder zum Ersatz des Wertes ist ausgeschlossen, soweit der Empfänger nicht mehr bereichert ist. Entreicherung tritt ein, wenn der ursprüngliche Vermögensvorteil ohne adäquaten Ausgleich wieder weggefallen ist. Konsumptive Verausgabung (Urlaub, verbrauchte Lebensmittel) führt zur Entreicherung; Investitionen (Kauf eines Gegenstands) nur, wenn der Gegensatz noch im Vermögen vorhanden ist. Die Einrede steht nur dem gutgläubigen Bereicherungsschuldner zu (Gegenschluss aus § 819 BGB).",
    sourceIds: ["bgb818"],
    sources: [
      { id: "bgb818", label: "§ 818 BGB", url: "https://www.gesetze-im-internet.de/bgb/__818.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["bereicherungsrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-040",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Sachenrecht - EBV",
    title: "Herausgabepflicht (§ 985 BGB) — Anspruchsgegner",
    prompt:
      "Nach § 985 BGB kann der Eigentümer die Herausgabe der Sache vom ___ verlangen.",
    options: [
      "Schädiger",
      "Besitzer",
      "Veräußerer",
      "Gläubiger",
    ],
    answers: [1],
    explanation:
      "§ 985 BGB: Der Eigentümer kann von dem Besitzer die Herausgabe der Sache verlangen. Anspruchsgegner ist der Besitzer — also derjenige, der die tatsächliche Sachherrschaft innehat (unmittelbarer Besitzer, § 854 BGB) oder über einen anderen ausübt (mittelbarer Besitzer, § 868 BGB). Gegen den Besitzdiener (§ 855 BGB) richtet sich der Anspruch nicht, da dieser kein Besitzer im Rechtssinne ist.",
    sourceIds: ["bgb985"],
    sources: [
      { id: "bgb985", label: "§ 985 BGB", url: "https://www.gesetze-im-internet.de/bgb/__985.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["sachenrecht", "ebv"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-041",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Sachenrecht - Mobiliareigentum",
    title: "Besitzkonstitut — ersetztes Tatbestandsmerkmal (§ 930 BGB)",
    prompt:
      "§ 930 BGB ersetzt beim Besitzkonstitut das Tatbestandsmerkmal der ___ durch die Vereinbarung eines Besitzmittlungsverhältnisses.",
    options: [
      "Einigung",
      "Übergabe",
      "Berechtigung",
      "Gutgläubigkeit",
    ],
    answers: [1],
    explanation:
      "§ 930 BGB: Ist der Eigentümer im Besitz der Sache, so kann die Übergabe dadurch ersetzt werden, dass zwischen dem Veräußerer und dem Erwerber ein Rechtsverhältnis vereinbart wird, vermöge dessen der Erwerber den mittelbaren Besitz erlangt. Das Besitzkonstitut ersetzt also die Übergabe (das physische Übergabeelement des § 929 S. 1 BGB). Die Einigung über den Eigentumsübergang bleibt daneben erforderlich.",
    sourceIds: ["bgb930"],
    sources: [
      { id: "bgb930", label: "§ 930 BGB", url: "https://www.gesetze-im-internet.de/bgb/__930.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Lücke",
    skillTags: ["sachenrecht", "eigentumserwerb"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-sachen-042",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Deliktsrecht",
    title: "Haftung für Verrichtungsgehilfen — Tatbestand (§ 831 Abs. 1 S. 1 BGB)",
    prompt:
      "Nach § 831 Abs. 1 S. 1 BGB haftet, wer einen anderen zur ___ bestellt, für den von diesem in Ausführung der Verrichtung einem Dritten widerrechtlich zugefügten Schaden.",
    options: [
      "Ausbildung",
      "Verrichtung",
      "Stellvertretung",
      "Geschäftsführung",
    ],
    answers: [1],
    explanation:
      "§ 831 Abs. 1 S. 1 BGB: Wer einen anderen zur Verrichtung bestellt, ist zum Ersatz des Schadens verpflichtet, den der andere in Ausführung der Verrichtung einem Dritten widerrechtlich zufügt. Verrichtung ist jede weisungsabhängige Tätigkeit; der Verrichtungsgehilfe (im Gegensatz zum selbstständigen Unternehmer) muss den Weisungen des Geschäftsherrn unterworfen sein. In Ausführung der Verrichtung bedeutet, dass ein innerer sachlicher Zusammenhang zwischen Verrichtung und schädigender Handlung bestehen muss.",
    sourceIds: ["bgb831"],
    sources: [
      { id: "bgb831", label: "§ 831 BGB", url: "https://www.gesetze-im-internet.de/bgb/__831.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["deliktsrecht"],
    reviewStatus: "beta-ready",
  },
];
